import { Check, Clock, PauseCircle } from "lucide-react";

import { Callout } from "@/components/ui/Display.jsx";
import { formatDuration } from "@/lib/format.js";
import { toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

/*
 * The three headline numbers, and an honest note about what they are.
 *
 * html-markup (copying UserSummaryCard.js) says "Total Work Time is a subset of
 * Total Tracked Time". It is not — see lib/userDetail.js. Tracked comes from
 * WebsiteAppUsage, Work from TimeEntry, and the desktop produces them on two
 * independent pipelines, so they can disagree in either direction. The note
 * below describes where each number comes from and reports the comparison the
 * page actually measured, which is both true and more useful: a large gap is a
 * sign the two pipelines have desynchronised on that machine.
 */
export default function HeroStats({ stats }) {
    const exceeds = stats.agreement != null && stats.agreement > 100;

    return (
        <>
            <section className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
                <Hero
                    label="Total Tracked Time"
                    tone="brand"
                    value={formatDuration(stats.tracked)}
                    icon={<Clock className="size-4" strokeWidth={2} />}
                    hint={`Across ${stats.apps} application${stats.apps === 1 ? "" : "s"}, ${stats.browsers} browser${stats.browsers === 1 ? "" : "s"} and ${stats.sites} website${stats.sites === 1 ? "" : "s"}`}
                    tip="Active application and website usage, summed from the desktop's usage records."
                    testId="tracked"
                />
                <Hero
                    label="Total Work Time"
                    tone="emerald"
                    value={formatDuration(stats.work)}
                    icon={<Check className="size-4" strokeWidth={2.2} />}
                    hint={
                        stats.agreement == null
                            ? "Measured from time entries"
                            : exceeds
                              ? `${stats.agreement - 100}% MORE than tracked usage`
                              : `${stats.agreement}% of tracked usage`
                    }
                    tip="Summed from the start/stop time entries, a separate pipeline from usage."
                    testId="work"
                />
                <Hero
                    label="Idle Time"
                    tone="rose"
                    value={formatDuration(stats.idle)}
                    icon={<PauseCircle className="size-4" strokeWidth={2} />}
                    hint="Measured separately · included in neither number above"
                    tip="Periods with no keyboard or mouse input while a time entry was running."
                    testId="idle"
                />
            </section>

            <Callout tone={exceeds ? "warning" : "info"} data-hero-note>
                <p>
                    <strong className="font-semibold">Total Tracked Time</strong> comes from
                    application and website usage;{" "}
                    <strong className="font-semibold">Total Work Time</strong> comes from time
                    entries. They are two independent pipelines, so neither contains the other —{" "}
                    {stats.agreement == null ? (
                        "there is not enough data in this range to compare them."
                    ) : exceeds ? (
                        <>
                            here work time is <strong className="font-semibold">higher</strong> than
                            tracked usage, which usually means usage records failed to upload while
                            the sessions kept running.
                        </>
                    ) : (
                        <>
                            here work time is {stats.agreement}% of tracked usage.
                        </>
                    )}{" "}
                    <strong className="font-semibold">Idle Time</strong> is a third pipeline and is
                    not part of either.
                </p>
            </Callout>
        </>
    );
}

function Hero({ label, value, hint, tone, icon, tip, testId }) {
    const t = toneOf(tone);

    return (
        <article
            data-hero={testId}
            className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4.5 shadow-xs dark:border-slate-800 dark:bg-slate-900"
        >
            <span className={cx("absolute inset-y-0 left-0 w-0.5", t.edge)} aria-hidden="true" />

            <div className="flex items-center gap-2">
                <span className={cx("grid size-7 shrink-0 place-items-center rounded-lg", t.icon)}>
                    {icon}
                </span>
                <span className="text-[12.5px] font-medium text-slate-600 dark:text-slate-400">
                    {label}
                </span>
                <span
                    title={tip}
                    className="ml-auto cursor-help text-slate-300 dark:text-slate-600"
                    aria-label={tip}
                >
                    <svg
                        className="size-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 16v-4M12 8h.01" />
                    </svg>
                </span>
            </div>

            <p
                data-hero-value
                className={cx("mt-2.5 text-[26px] leading-none font-semibold tracking-tight", t.text)}
            >
                {value}
            </p>
            <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500">{hint}</p>
        </article>
    );
}
